/**
 * 🎸 CHORD ENGINE — Motor Extendido de Acordes para Bordona
 *
 * Dos modalidades:
 * 1. CLÁSICO: formas predefinidas (open chords, barre shapes movibles)
 * 2. EXPLORADOR: generación algorítmica de voicings con filtros
 */

import {
  type NoteName,
  type NeckPosition,
  noteToSemitones,
  semitonesToNote,
  getNoteAtPosition,
  shouldUseFlats,
  OPEN_STRINGS,
} from "./guitarEngine";

// ============================================
// TIPOS
// ============================================

export type ExtendedChordQuality =
  | "major" | "minor" | "dim" | "aug"
  | "sus2" | "sus4" | "sus2sus4"
  | "maj7" | "m7" | "7" | "m7b5" | "dim7" | "aug7" | "mMaj7"
  | "add2" | "add4" | "add9" | "add11"
  | "6" | "m6"
  | "9" | "m9" | "maj9"
  | "11" | "m11" | "maj11"
  | "13" | "m13" | "maj13"
  | "7sus4" | "7sus2" | "7b5" | "7sharp5"
  | "9b5" | "9sharp5" | "m9b5"
  | "13b9" | "13sharp9" | "7b9" | "7sharp9";

export type InversionType = "fundamental" | "first" | "second" | "third";
export type VoicingMode = "classic" | "explorer";

export interface ChordDefinition {
  quality: ExtendedChordQuality;
  label: string;
  category: "triada" | "tetrada" | "tension" | "alterada";
  intervals: number[]; // semitonos desde la raíz
  degreeNames: string[];
}

export interface PredefinedShape {
  id: string;
  name: string;
  quality: ExtendedChordQuality;
  rootString: number; // cuerda donde está la raíz
  rootFret: number;   // traste de la raíz (0 = al aire)
  positions: { string: number; fret: number; finger: number; degree: string }[];
  barre?: { fromString: number; toString: number; fret: number }; // cejilla
  mutedStrings?: number[];
  isMovable: boolean;
  baseRoot?: NoteName; // para acordes abiertos, la raíz original
  description?: string;
}

export interface ChordVoicingResult {
  positions: NeckPosition[];
  mutedStrings: number[];
  barre?: { fromString: number; toString: number; fret: number };
  shapeName?: string;
  description?: string;
  stringCount: number;
  hasOpenStrings: boolean;
  maxFret: number;
  span: number;
}

export interface ExplorerFilter {
  selectedStrings: number[]; // cuerdas que el usuario quiere usar, ej: [3,2,1]
  allowOpenStrings: boolean;
  allowBarre: boolean;
  maxFret: number;
  maxSpan: number;
  inversion: InversionType;
  voicingType: "all" | "close" | "spread" | "drop2" | "drop3";
}

// ============================================
// CATÁLOGO DE CALIDADES
// ============================================

export const CHORD_CATALOG: ChordDefinition[] = [
  // Tríadas
  { quality: "major",   label: "Mayor",       category: "triada", intervals: [0, 4, 7],        degreeNames: ["R", "3", "5"] },
  { quality: "minor",   label: "Menor",       category: "triada", intervals: [0, 3, 7],        degreeNames: ["R", "b3", "5"] },
  { quality: "dim",     label: "Disminuido",  category: "triada", intervals: [0, 3, 6],        degreeNames: ["R", "b3", "b5"] },
  { quality: "aug",     label: "Aumentado",   category: "triada", intervals: [0, 4, 8],        degreeNames: ["R", "3", "#5"] },
  { quality: "sus2",    label: "Sus2",        category: "triada", intervals: [0, 2, 7],        degreeNames: ["R", "2", "5"] },
  { quality: "sus4",    label: "Sus4",        category: "triada", intervals: [0, 5, 7],        degreeNames: ["R", "4", "5"] },
  // Tétradas
  { quality: "maj7",    label: "Maj7",        category: "tetrada", intervals: [0, 4, 7, 11],    degreeNames: ["R", "3", "5", "7"] },
  { quality: "7",       label: "7 (Dominante)", category: "tetrada", intervals: [0, 4, 7, 10],  degreeNames: ["R", "3", "5", "b7"] },
  { quality: "m7",      label: "m7",          category: "tetrada", intervals: [0, 3, 7, 10],    degreeNames: ["R", "b3", "5", "b7"] },
  { quality: "m7b5",    label: "m7♭5 (Semidis)", category: "tetrada", intervals: [0, 3, 6, 10], degreeNames: ["R", "b3", "b5", "b7"] },
  { quality: "dim7",    label: "dim7",        category: "tetrada", intervals: [0, 3, 6, 9],     degreeNames: ["R", "b3", "b5", "bb7"] },
  { quality: "aug7",    label: "7♯5",         category: "tetrada", intervals: [0, 4, 8, 10],    degreeNames: ["R", "3", "#5", "b7"] },
  { quality: "mMaj7",   label: "mMaj7",       category: "tetrada", intervals: [0, 3, 7, 11],    degreeNames: ["R", "b3", "5", "7"] },
  { quality: "7sus4",   label: "7sus4",       category: "tetrada", intervals: [0, 5, 7, 10],    degreeNames: ["R", "4", "5", "b7"] },
  { quality: "7sus2",   label: "7sus2",       category: "tetrada", intervals: [0, 2, 7, 10],    degreeNames: ["R", "2", "5", "b7"] },
  { quality: "7b5",     label: "7♭5",         category: "tetrada", intervals: [0, 4, 6, 10],    degreeNames: ["R", "3", "b5", "b7"] },
  { quality: "7sharp5", label: "7♯5",         category: "tetrada", intervals: [0, 4, 8, 10],    degreeNames: ["R", "3", "#5", "b7"] },
  // Adiciones
  { quality: "add2",    label: "add2",        category: "tension", intervals: [0, 2, 4, 7],     degreeNames: ["R", "2", "3", "5"] },
  { quality: "add4",    label: "add4",        category: "tension", intervals: [0, 4, 5, 7],     degreeNames: ["R", "3", "4", "5"] },
  { quality: "add9",    label: "add9",        category: "tension", intervals: [0, 4, 7, 14],    degreeNames: ["R", "3", "5", "9"] },
  { quality: "add11",   label: "add11",       category: "tension", intervals: [0, 4, 7, 17],    degreeNames: ["R", "3", "5", "11"] },
  { quality: "6",       label: "6",           category: "tension", intervals: [0, 4, 7, 9],     degreeNames: ["R", "3", "5", "6"] },
  { quality: "m6",      label: "m6",          category: "tension", intervals: [0, 3, 7, 9],     degreeNames: ["R", "b3", "5", "6"] },
  // Tensiones 9, 11, 13
  { quality: "9",       label: "9",           category: "tension", intervals: [0, 4, 7, 10, 14], degreeNames: ["R", "3", "5", "b7", "9"] },
  { quality: "m9",      label: "m9",          category: "tension", intervals: [0, 3, 7, 10, 14], degreeNames: ["R", "b3", "5", "b7", "9"] },
  { quality: "maj9",    label: "Maj9",        category: "tension", intervals: [0, 4, 7, 11, 14], degreeNames: ["R", "3", "5", "7", "9"] },
  { quality: "11",      label: "11",          category: "tension", intervals: [0, 4, 7, 10, 14, 17], degreeNames: ["R", "3", "5", "b7", "9", "11"] },
  { quality: "m11",     label: "m11",         category: "tension", intervals: [0, 3, 7, 10, 14, 17], degreeNames: ["R", "b3", "5", "b7", "9", "11"] },
  { quality: "maj11",   label: "Maj11",       category: "tension", intervals: [0, 4, 7, 11, 14, 17], degreeNames: ["R", "3", "5", "7", "9", "11"] },
  { quality: "13",      label: "13",          category: "tension", intervals: [0, 4, 7, 10, 14, 17, 21], degreeNames: ["R", "3", "5", "b7", "9", "11", "13"] },
  { quality: "m13",     label: "m13",         category: "tension", intervals: [0, 3, 7, 10, 14, 17, 21], degreeNames: ["R", "b3", "5", "b7", "9", "11", "13"] },
  { quality: "maj13",   label: "Maj13",       category: "tension", intervals: [0, 4, 7, 11, 14, 17, 21], degreeNames: ["R", "3", "5", "7", "9", "11", "13"] },
  // Alteradas
  { quality: "7b9",     label: "7♭9",         category: "alterada", intervals: [0, 4, 7, 10, 13], degreeNames: ["R", "3", "5", "b7", "b9"] },
  { quality: "7sharp9", label: "7♯9",         category: "alterada", intervals: [0, 4, 7, 10, 15], degreeNames: ["R", "3", "5", "b7", "#9"] },
  { quality: "9b5",     label: "9♭5",         category: "alterada", intervals: [0, 4, 6, 10, 14], degreeNames: ["R", "3", "b5", "b7", "9"] },
  { quality: "9sharp5", label: "9♯5",         category: "alterada", intervals: [0, 4, 8, 10, 14], degreeNames: ["R", "3", "#5", "b7", "9"] },
  { quality: "m9b5",    label: "m9♭5",        category: "alterada", intervals: [0, 3, 6, 10, 14], degreeNames: ["R", "b3", "b5", "b7", "9"] },
  { quality: "13b9",    label: "13♭9",        category: "alterada", intervals: [0, 4, 7, 10, 13, 21], degreeNames: ["R", "3", "5", "b7", "b9", "13"] },
  { quality: "13sharp9", label: "13♯9",       category: "alterada", intervals: [0, 4, 7, 10, 15, 21], degreeNames: ["R", "3", "5", "b7", "#9", "13"] },
];

export function getChordDef(quality: ExtendedChordQuality): ChordDefinition | undefined {
  return CHORD_CATALOG.find((c) => c.quality === quality);
}

// ============================================
// FORMAS PREDEFINIDAS (CLÁSICO)
// ============================================

/**
 * Formas predefinidas de acordes.
 * Para shapes movibles, se usa rootFret como offset y se transponen.
 * Para shapes abiertos, baseRoot indica la raíz original.
 */
export const PREDEFINED_SHAPES: PredefinedShape[] = [
  // ─── ACORDES ABIERTOS (no movibles) ───
  // A Mayor
  {
    id: "a-open", name: "A (Abierto)", quality: "major", rootString: 5, rootFret: 0,
    positions: [
      { string: 4, fret: 2, finger: 2, degree: "5" },
      { string: 3, fret: 2, finger: 3, degree: "R" },
      { string: 2, fret: 2, finger: 4, degree: "3" },
    ],
    isMovable: false, baseRoot: "A",
    description: "Clásico A mayor abierto",
  },
  // G Mayor
  {
    id: "g-open", name: "G (Abierto)", quality: "major", rootString: 6, rootFret: 3,
    positions: [
      { string: 6, fret: 3, finger: 2, degree: "R" },
      { string: 5, fret: 2, finger: 1, degree: "3" },
      { string: 1, fret: 3, finger: 3, degree: "R" },
      { string: 2, fret: 0, finger: 0, degree: "5" },
      { string: 3, fret: 0, finger: 0, degree: "R" },
      { string: 4, fret: 0, finger: 0, degree: "5" },
    ],
    isMovable: false, baseRoot: "G",
    description: "G mayor completo",
  },
  // E Mayor
  {
    id: "e-open", name: "E (Abierto)", quality: "major", rootString: 6, rootFret: 0,
    positions: [
      { string: 6, fret: 0, finger: 0, degree: "R" },
      { string: 5, fret: 2, finger: 2, degree: "5" },
      { string: 4, fret: 2, finger: 3, degree: "R" },
      { string: 3, fret: 1, finger: 1, degree: "3" },
      { string: 2, fret: 0, finger: 0, degree: "5" },
      { string: 1, fret: 0, finger: 0, degree: "R" },
    ],
    isMovable: false, baseRoot: "E",
    description: "E mayor abierto",
  },
  // D Mayor
  {
    id: "d-open", name: "D (Abierto)", quality: "major", rootString: 4, rootFret: 0,
    positions: [
      { string: 4, fret: 0, finger: 0, degree: "R" },
      { string: 3, fret: 2, finger: 1, degree: "5" },
      { string: 2, fret: 3, finger: 3, degree: "R" },
      { string: 1, fret: 2, finger: 2, degree: "3" },
    ],
    mutedStrings: [6, 5],
    isMovable: false, baseRoot: "D",
    description: "D mayor abierto",
  },
  // Am
  {
    id: "am-open", name: "Am (Abierto)", quality: "minor", rootString: 5, rootFret: 0,
    positions: [
      { string: 4, fret: 2, finger: 2, degree: "5" },
      { string: 3, fret: 2, finger: 3, degree: "R" },
      { string: 2, fret: 1, finger: 1, degree: "b3" },
    ],
    isMovable: false, baseRoot: "A",
    description: "A menor abierto",
  },
  // Em
  {
    id: "em-open", name: "Em (Abierto)", quality: "minor", rootString: 6, rootFret: 0,
    positions: [
      { string: 6, fret: 0, finger: 0, degree: "R" },
      { string: 5, fret: 2, finger: 2, degree: "5" },
      { string: 4, fret: 2, finger: 3, degree: "R" },
      { string: 3, fret: 0, finger: 0, degree: "b3" },
      { string: 2, fret: 0, finger: 0, degree: "5" },
      { string: 1, fret: 0, finger: 0, degree: "R" },
    ],
    isMovable: false, baseRoot: "E",
    description: "E menor abierto",
  },
  // Dm
  {
    id: "dm-open", name: "Dm (Abierto)", quality: "minor", rootString: 4, rootFret: 0,
    positions: [
      { string: 4, fret: 0, finger: 0, degree: "R" },
      { string: 3, fret: 2, finger: 2, degree: "5" },
      { string: 2, fret: 3, finger: 3, degree: "R" },
      { string: 1, fret: 1, finger: 1, degree: "b3" },
    ],
    mutedStrings: [6, 5],
    isMovable: false, baseRoot: "D",
    description: "D menor abierto",
  },
  // C7
  {
    id: "c7-open", name: "C7 (Abierto)", quality: "7", rootString: 5, rootFret: 3,
    positions: [
      { string: 5, fret: 3, finger: 3, degree: "R" },
      { string: 4, fret: 2, finger: 2, degree: "3" },
      { string: 3, fret: 3, finger: 4, degree: "b7" },
      { string: 2, fret: 1, finger: 1, degree: "5" },
      { string: 1, fret: 0, finger: 0, degree: "R" },
    ],
    mutedStrings: [6],
    isMovable: false, baseRoot: "C",
    description: "C7 dominante abierto",
  },
  // A7
  {
    id: "a7-open", name: "A7 (Abierto)", quality: "7", rootString: 5, rootFret: 0,
    positions: [
      { string: 4, fret: 2, finger: 2, degree: "5" },
      { string: 3, fret: 0, finger: 0, degree: "R" },
      { string: 2, fret: 2, finger: 3, degree: "3" },
      { string: 1, fret: 0, finger: 0, degree: "b7" },
    ],
    isMovable: false, baseRoot: "A",
    description: "A7 dominante abierto",
  },
  // E7
  {
    id: "e7-open", name: "E7 (Abierto)", quality: "7", rootString: 6, rootFret: 0,
    positions: [
      { string: 6, fret: 0, finger: 0, degree: "R" },
      { string: 5, fret: 2, finger: 2, degree: "5" },
      { string: 4, fret: 0, finger: 0, degree: "R" },
      { string: 3, fret: 1, finger: 1, degree: "3" },
      { string: 2, fret: 0, finger: 0, degree: "b7" },
      { string: 1, fret: 0, finger: 0, degree: "R" },
    ],
    isMovable: false, baseRoot: "E",
    description: "E7 dominante abierto",
  },
  // D7
  {
    id: "d7-open", name: "D7 (Abierto)", quality: "7", rootString: 4, rootFret: 0,
    positions: [
      { string: 4, fret: 0, finger: 0, degree: "R" },
      { string: 3, fret: 2, finger: 2, degree: "5" },
      { string: 2, fret: 1, finger: 1, degree: "b7" },
      { string: 1, fret: 2, finger: 3, degree: "3" },
    ],
    mutedStrings: [6, 5],
    isMovable: false, baseRoot: "D",
    description: "D7 dominante abierto",
  },
  // G7
  {
    id: "g7-open", name: "G7 (Abierto)", quality: "7", rootString: 6, rootFret: 3,
    positions: [
      { string: 6, fret: 3, finger: 2, degree: "R" },
      { string: 5, fret: 2, finger: 1, degree: "3" },
      { string: 1, fret: 1, finger: 1, degree: "b7" },
      { string: 2, fret: 0, finger: 0, degree: "5" },
    ],
    mutedStrings: [4, 3],
    isMovable: false, baseRoot: "G",
    description: "G7 dominante abierto (3 cuerdas)",
  },
  // Dsus2
  {
    id: "dsus2-open", name: "Dsus2 (Abierto)", quality: "sus2", rootString: 4, rootFret: 0,
    positions: [
      { string: 4, fret: 0, finger: 0, degree: "R" },
      { string: 3, fret: 2, finger: 1, degree: "5" },
      { string: 2, fret: 3, finger: 3, degree: "R" },
      { string: 1, fret: 0, finger: 0, degree: "2" },
    ],
    mutedStrings: [6, 5],
    isMovable: false, baseRoot: "D",
    description: "Dsus2 abierto",
  },
  // Dsus4
  {
    id: "dsus4-open", name: "Dsus4 (Abierto)", quality: "sus4", rootString: 4, rootFret: 0,
    positions: [
      { string: 4, fret: 0, finger: 0, degree: "R" },
      { string: 3, fret: 2, finger: 1, degree: "5" },
      { string: 2, fret: 3, finger: 3, degree: "R" },
      { string: 1, fret: 3, finger: 4, degree: "4" },
    ],
    mutedStrings: [6, 5],
    isMovable: false, baseRoot: "D",
    description: "Dsus4 abierto",
  },

  // ─── SHAPES MOVIBLES (Barre) ───
  // E-shape Mayor (cejilla en forma de E)
  {
    id: "e-shape-maj", name: "Shape E (Mayor)", quality: "major", rootString: 6, rootFret: 0,
    positions: [
      { string: 6, fret: 0, finger: 0, degree: "R" },
      { string: 5, fret: 2, finger: 3, degree: "5" },
      { string: 4, fret: 2, finger: 4, degree: "R" },
      { string: 3, fret: 1, finger: 2, degree: "3" },
      { string: 2, fret: 0, finger: 0, degree: "5" },
      { string: 1, fret: 0, finger: 0, degree: "R" },
    ],
    barre: { fromString: 6, toString: 1, fret: 0 },
    isMovable: true, baseRoot: "E",
    description: "Shape E mayor movible (F, G♭, G...)",
  },
  // A-shape Mayor (cejilla en forma de A)
  {
    id: "a-shape-maj", name: "Shape A (Mayor)", quality: "major", rootString: 5, rootFret: 0,
    positions: [
      { string: 5, fret: 0, finger: 0, degree: "R" },
      { string: 4, fret: 2, finger: 3, degree: "5" },
      { string: 3, fret: 2, finger: 4, degree: "R" },
      { string: 2, fret: 2, finger: 4, degree: "3" },
      { string: 1, fret: 0, finger: 0, degree: "5" },
    ],
    barre: { fromString: 5, toString: 1, fret: 0 },
    mutedStrings: [6],
    isMovable: true, baseRoot: "A",
    description: "Shape A mayor movible (B♭, B, C...)",
  },
  // D-shape Mayor
  {
    id: "d-shape-maj", name: "Shape D (Mayor)", quality: "major", rootString: 4, rootFret: 0,
    positions: [
      { string: 4, fret: 0, finger: 0, degree: "R" },
      { string: 3, fret: 2, finger: 2, degree: "5" },
      { string: 2, fret: 3, finger: 3, degree: "R" },
      { string: 1, fret: 2, finger: 1, degree: "3" },
    ],
    mutedStrings: [6, 5],
    isMovable: true, baseRoot: "D",
    description: "Shape D mayor movible (E♭, E, F...)",
  },
  // C-shape Mayor
  {
    id: "c-shape-maj", name: "Shape C (Mayor)", quality: "major", rootString: 5, rootFret: 3,
    positions: [
      { string: 5, fret: 3, finger: 3, degree: "R" },
      { string: 4, fret: 2, finger: 2, degree: "3" },
      { string: 3, fret: 0, finger: 0, degree: "5" },
      { string: 2, fret: 1, finger: 1, degree: "R" },
      { string: 1, fret: 0, finger: 0, degree: "3" },
    ],
    mutedStrings: [6],
    isMovable: true, baseRoot: "C",
    description: "Shape C mayor movible",
  },
  // G-shape Mayor
  {
    id: "g-shape-maj", name: "Shape G (Mayor)", quality: "major", rootString: 6, rootFret: 3,
    positions: [
      { string: 6, fret: 3, finger: 2, degree: "R" },
      { string: 5, fret: 2, finger: 1, degree: "3" },
      { string: 4, fret: 0, finger: 0, degree: "5" },
      { string: 3, fret: 0, finger: 0, degree: "R" },
      { string: 2, fret: 0, finger: 0, degree: "5" },
      { string: 1, fret: 3, finger: 3, degree: "R" },
    ],
    isMovable: true, baseRoot: "G",
    description: "Shape G mayor movible",
  },
  // E-shape Menor
  {
    id: "e-shape-min", name: "Shape E (Menor)", quality: "minor", rootString: 6, rootFret: 0,
    positions: [
      { string: 6, fret: 0, finger: 0, degree: "R" },
      { string: 5, fret: 2, finger: 3, degree: "5" },
      { string: 4, fret: 2, finger: 4, degree: "R" },
      { string: 3, fret: 0, finger: 0, degree: "b3" },
      { string: 2, fret: 0, finger: 0, degree: "5" },
      { string: 1, fret: 0, finger: 0, degree: "R" },
    ],
    barre: { fromString: 6, toString: 1, fret: 0 },
    isMovable: true, baseRoot: "E",
    description: "Shape E menor movible (Fm, G♭m, Gm...)",
  },
  // A-shape Menor
  {
    id: "a-shape-min", name: "Shape A (Menor)", quality: "minor", rootString: 5, rootFret: 0,
    positions: [
      { string: 5, fret: 0, finger: 0, degree: "R" },
      { string: 4, fret: 2, finger: 3, degree: "5" },
      { string: 3, fret: 2, finger: 4, degree: "R" },
      { string: 2, fret: 1, finger: 2, degree: "b3" },
      { string: 1, fret: 0, finger: 0, degree: "5" },
    ],
    barre: { fromString: 5, toString: 1, fret: 0 },
    mutedStrings: [6],
    isMovable: true, baseRoot: "A",
    description: "Shape A menor movible (B♭m, Bm, Cm...)",
  },
  // D-shape Menor
  {
    id: "d-shape-min", name: "Shape D (Menor)", quality: "minor", rootString: 4, rootFret: 0,
    positions: [
      { string: 4, fret: 0, finger: 0, degree: "R" },
      { string: 3, fret: 2, finger: 2, degree: "5" },
      { string: 2, fret: 3, finger: 3, degree: "R" },
      { string: 1, fret: 1, finger: 1, degree: "b3" },
    ],
    mutedStrings: [6, 5],
    isMovable: true, baseRoot: "D",
    description: "Shape D menor movible",
  },
  // E-shape 7
  {
    id: "e-shape-7", name: "Shape E (7)", quality: "7", rootString: 6, rootFret: 0,
    positions: [
      { string: 6, fret: 0, finger: 0, degree: "R" },
      { string: 5, fret: 2, finger: 3, degree: "5" },
      { string: 4, fret: 0, finger: 0, degree: "R" },
      { string: 3, fret: 1, finger: 1, degree: "3" },
      { string: 2, fret: 0, finger: 0, degree: "b7" },
      { string: 1, fret: 0, finger: 0, degree: "R" },
    ],
    barre: { fromString: 6, toString: 1, fret: 0 },
    isMovable: true, baseRoot: "E",
    description: "Shape E7 movible",
  },
  // A-shape 7
  {
    id: "a-shape-7", name: "Shape A (7)", quality: "7", rootString: 5, rootFret: 0,
    positions: [
      { string: 5, fret: 0, finger: 0, degree: "R" },
      { string: 4, fret: 2, finger: 3, degree: "5" },
      { string: 3, fret: 0, finger: 0, degree: "R" },
      { string: 2, fret: 2, finger: 4, degree: "3" },
      { string: 1, fret: 0, finger: 0, degree: "b7" },
    ],
    barre: { fromString: 5, toString: 1, fret: 0 },
    mutedStrings: [6],
    isMovable: true, baseRoot: "A",
    description: "Shape A7 movible",
  },
  // E-shape Maj7
  {
    id: "e-shape-maj7", name: "Shape E (Maj7)", quality: "maj7", rootString: 6, rootFret: 0,
    positions: [
      { string: 6, fret: 0, finger: 0, degree: "R" },
      { string: 5, fret: 2, finger: 3, degree: "5" },
      { string: 4, fret: 1, finger: 2, degree: "3" },
      { string: 3, fret: 1, finger: 1, degree: "7" },
      { string: 2, fret: 0, finger: 0, degree: "5" },
      { string: 1, fret: 0, finger: 0, degree: "R" },
    ],
    barre: { fromString: 6, toString: 1, fret: 0 },
    isMovable: true, baseRoot: "E",
    description: "Shape E Maj7 movible",
  },
  // A-shape Maj7
  {
    id: "a-shape-maj7", name: "Shape A (Maj7)", quality: "maj7", rootString: 5, rootFret: 0,
    positions: [
      { string: 5, fret: 0, finger: 0, degree: "R" },
      { string: 4, fret: 2, finger: 3, degree: "5" },
      { string: 3, fret: 1, finger: 2, degree: "7" },
      { string: 2, fret: 2, finger: 4, degree: "3" },
      { string: 1, fret: 0, finger: 0, degree: "5" },
    ],
    barre: { fromString: 5, toString: 1, fret: 0 },
    mutedStrings: [6],
    isMovable: true, baseRoot: "A",
    description: "Shape A Maj7 movible",
  },
  // E-shape m7
  {
    id: "e-shape-m7", name: "Shape E (m7)", quality: "m7", rootString: 6, rootFret: 0,
    positions: [
      { string: 6, fret: 0, finger: 0, degree: "R" },
      { string: 5, fret: 2, finger: 3, degree: "5" },
      { string: 4, fret: 0, finger: 0, degree: "b3" },
      { string: 3, fret: 0, finger: 0, degree: "b7" },
      { string: 2, fret: 0, finger: 0, degree: "5" },
      { string: 1, fret: 0, finger: 0, degree: "R" },
    ],
    barre: { fromString: 6, toString: 1, fret: 0 },
    isMovable: true, baseRoot: "E",
    description: "Shape E m7 movible",
  },
  // A-shape m7
  {
    id: "a-shape-m7", name: "Shape A (m7)", quality: "m7", rootString: 5, rootFret: 0,
    positions: [
      { string: 5, fret: 0, finger: 0, degree: "R" },
      { string: 4, fret: 2, finger: 3, degree: "5" },
      { string: 3, fret: 0, finger: 0, degree: "b3" },
      { string: 2, fret: 1, finger: 1, degree: "b7" },
      { string: 1, fret: 0, finger: 0, degree: "5" },
    ],
    barre: { fromString: 5, toString: 1, fret: 0 },
    mutedStrings: [6],
    isMovable: true, baseRoot: "A",
    description: "Shape A m7 movible",
  },
  // E-shape m7b5
  {
    id: "e-shape-m7b5", name: "Shape E (m7♭5)", quality: "m7b5", rootString: 6, rootFret: 0,
    positions: [
      { string: 6, fret: 0, finger: 0, degree: "R" },
      { string: 5, fret: 1, finger: 1, degree: "b5" },
      { string: 4, fret: 0, finger: 0, degree: "b3" },
      { string: 3, fret: 0, finger: 0, degree: "b7" },
      { string: 2, fret: 0, finger: 0, degree: "b5" },
      { string: 1, fret: 0, finger: 0, degree: "R" },
    ],
    barre: { fromString: 6, toString: 1, fret: 0 },
    isMovable: true, baseRoot: "E",
    description: "Shape E m7♭5 (semidisminuido) movible",
  },
  // A-shape m7b5
  {
    id: "a-shape-m7b5", name: "Shape A (m7♭5)", quality: "m7b5", rootString: 5, rootFret: 0,
    positions: [
      { string: 5, fret: 0, finger: 0, degree: "R" },
      { string: 4, fret: 1, finger: 1, degree: "b5" },
      { string: 3, fret: 0, finger: 0, degree: "b3" },
      { string: 2, fret: 1, finger: 2, degree: "b7" },
      { string: 1, fret: 0, finger: 0, degree: "b5" },
    ],
    barre: { fromString: 5, toString: 1, fret: 0 },
    mutedStrings: [6],
    isMovable: true, baseRoot: "A",
    description: "Shape A m7♭5 movible",
  },
  // D-shape 7 (CAGED D)
  {
    id: "d-shape-7", name: "Shape D (7)", quality: "7", rootString: 4, rootFret: 0,
    positions: [
      { string: 4, fret: 0, finger: 0, degree: "R" },
      { string: 3, fret: 2, finger: 2, degree: "5" },
      { string: 2, fret: 1, finger: 1, degree: "b7" },
      { string: 1, fret: 2, finger: 3, degree: "3" },
    ],
    mutedStrings: [6, 5],
    isMovable: true, baseRoot: "D",
    description: "Shape D7 movible",
  },
  // Power chord E-shape (5)
  {
    id: "e-shape-5", name: "Power (6 cuerdas)", quality: "major", rootString: 6, rootFret: 0,
    positions: [
      { string: 6, fret: 0, finger: 0, degree: "R" },
      { string: 5, fret: 2, finger: 2, degree: "5" },
      { string: 4, fret: 2, finger: 3, degree: "R" },
    ],
    mutedStrings: [3, 2, 1],
    isMovable: true, baseRoot: "E",
    description: "Power chord 3 cuerdas desde E",
  },
  // Power chord A-shape
  {
    id: "a-shape-5", name: "Power (5 cuerdas)", quality: "major", rootString: 5, rootFret: 0,
    positions: [
      { string: 5, fret: 0, finger: 0, degree: "R" },
      { string: 4, fret: 2, finger: 2, degree: "5" },
      { string: 3, fret: 2, finger: 3, degree: "R" },
    ],
    mutedStrings: [6, 2, 1],
    isMovable: true, baseRoot: "A",
    description: "Power chord 3 cuerdas desde A",
  },
  // ─── TENSIONES EN SHAPES CORTOS ───
  // maj9 en 4 cuerdas (shape D-base)
  {
    id: "maj9-4str", name: "Maj9 (4 cuerdas)", quality: "maj9", rootString: 4, rootFret: 0,
    positions: [
      { string: 4, fret: 0, finger: 0, degree: "R" },
      { string: 3, fret: 2, finger: 2, degree: "5" },
      { string: 2, fret: 1, finger: 1, degree: "7" },
      { string: 1, fret: 2, finger: 3, degree: "9" },
    ],
    mutedStrings: [6, 5],
    isMovable: true, baseRoot: "D",
    description: "Maj9 en 4 cuerdas agudas",
  },
  // 9 en 4 cuerdas
  {
    id: "dom9-4str", name: "9 (4 cuerdas)", quality: "9", rootString: 4, rootFret: 0,
    positions: [
      { string: 4, fret: 0, finger: 0, degree: "R" },
      { string: 3, fret: 2, finger: 2, degree: "5" },
      { string: 2, fret: 0, finger: 0, degree: "b7" },
      { string: 1, fret: 2, finger: 3, degree: "9" },
    ],
    mutedStrings: [6, 5],
    isMovable: true, baseRoot: "D",
    description: "9 dominante en 4 cuerdas agudas",
  },
  // m9 en 4 cuerdas
  {
    id: "m9-4str", name: "m9 (4 cuerdas)", quality: "m9", rootString: 4, rootFret: 0,
    positions: [
      { string: 4, fret: 0, finger: 0, degree: "R" },
      { string: 3, fret: 2, finger: 2, degree: "5" },
      { string: 2, fret: 0, finger: 0, degree: "b7" },
      { string: 1, fret: 1, finger: 1, degree: "9" },
    ],
    mutedStrings: [6, 5],
    isMovable: true, baseRoot: "D",
    description: "m9 en 4 cuerdas agudas",
  },
  // 6 en 3 cuerdas (triada + 6)
  {
    id: "maj6-3str", name: "6 (3 cuerdas)", quality: "6", rootString: 3, rootFret: 0,
    positions: [
      { string: 3, fret: 0, finger: 0, degree: "R" },
      { string: 2, fret: 2, finger: 2, degree: "6" },
      { string: 1, fret: 0, finger: 0, degree: "3" },
    ],
    mutedStrings: [6, 5, 4],
    isMovable: true, baseRoot: "G",
    description: "6 mayor en 3 cuerdas",
  },
  // sus2 en 3 cuerdas
  {
    id: "sus2-3str", name: "Sus2 (3 cuerdas)", quality: "sus2", rootString: 4, rootFret: 0,
    positions: [
      { string: 4, fret: 0, finger: 0, degree: "R" },
      { string: 3, fret: 2, finger: 2, degree: "5" },
      { string: 2, fret: 0, finger: 0, degree: "2" },
    ],
    mutedStrings: [6, 5, 1],
    isMovable: true, baseRoot: "D",
    description: "Sus2 en 3 cuerdas",
  },
  // sus4 en 3 cuerdas
  {
    id: "sus4-3str", name: "Sus4 (3 cuerdas)", quality: "sus4", rootString: 4, rootFret: 0,
    positions: [
      { string: 4, fret: 0, finger: 0, degree: "R" },
      { string: 3, fret: 2, finger: 2, degree: "5" },
      { string: 2, fret: 3, finger: 3, degree: "4" },
    ],
    mutedStrings: [6, 5, 1],
    isMovable: true, baseRoot: "D",
    description: "Sus4 en 3 cuerdas",
  },
];

// ============================================
// TRANSPOSICIÓN Y RESULTADO
// ============================================

/**
 * transpone una forma predefinida a una raíz dada.
 * Para shapes movibles: calcula el offset en semitonos y desplaza todos los trastes.
 * Para shapes abiertos: solo funciona si la raíz coincide con baseRoot.
 */
export function transposeShape(
  shape: PredefinedShape,
  targetRoot: NoteName
): ChordVoicingResult | null {
  if (!shape.isMovable) {
    // Solo mostrar si coincide exactamente
    if (shape.baseRoot === targetRoot) {
      return shapeToResult(shape);
    }
    return null;
  }

  const baseSemi = noteToSemitones(shape.baseRoot || "C");
  const targetSemi = noteToSemitones(targetRoot);
  let offset = targetSemi - baseSemi;
  // Si el offset es negativo, subir una octava para mantener posiciones jugables
  while (offset < 0) {
    offset += 12;
  }

  const newPositions = shape.positions.map((p) => ({
    string: p.string,
    fret: Math.max(0, p.fret + offset),
    finger: p.finger,
    degree: p.degree,
  }));

  const newBarre = shape.barre
    ? { ...shape.barre, fret: Math.max(0, shape.barre.fret + offset) }
    : undefined;

  const transposedShape: PredefinedShape = {
    ...shape,
    rootFret: Math.max(0, shape.rootFret + offset),
    positions: newPositions,
    barre: newBarre,
  };

  return shapeToResult(transposedShape);
}

function shapeToResult(shape: PredefinedShape): ChordVoicingResult {
  const noteMap = new Map<number, NoteName>();
  const neckPositions: NeckPosition[] = shape.positions.map((p) => {
    const noteData = getNoteAtPosition(p.string, p.fret);
    noteMap.set(p.string, noteData.note);
    return {
      string: p.string,
      fret: p.fret,
      note: noteData.note,
      degree: p.degree as any,
      highlight: p.degree === "R" ? "root" : p.degree === "5" ? "fifth" : "third",
      finger: p.finger,
    };
  });

  const pressed = shape.positions.filter((p) => p.fret > 0);
  const frets = pressed.map((p) => p.fret);
  const span = frets.length > 1 ? Math.max(...frets) - Math.min(...frets) : 0;

  return {
    positions: neckPositions,
    mutedStrings: shape.mutedStrings || [],
    barre: shape.barre,
    shapeName: shape.name,
    description: shape.description,
    stringCount: shape.positions.length,
    hasOpenStrings: shape.positions.some((p) => p.fret === 0),
    maxFret: Math.max(...shape.positions.map((p) => p.fret)),
    span,
  };
}

/**
 * Obtiene todas las formas predefinidas disponibles para una raíz y calidad.
 */
export function getClassicVoicings(
  root: NoteName,
  quality: ExtendedChordQuality
): ChordVoicingResult[] {
  const results: ChordVoicingResult[] = [];
  const seen = new Set<string>();

  for (const shape of PREDEFINED_SHAPES) {
    if (shape.quality !== quality) continue;
    const voicing = transposeShape(shape, root);
    if (!voicing) continue;

    // Evitar duplicados exactos
    const key = voicing.positions.map((p) => `${p.string}-${p.fret}`).join("|");
    if (seen.has(key)) continue;
    seen.add(key);

    results.push(voicing);
  }

  // Ordenar: abiertos primero, luego por traste
  results.sort((a, b) => {
    if (a.hasOpenStrings && !b.hasOpenStrings) return -1;
    if (!a.hasOpenStrings && b.hasOpenStrings) return 1;
    return a.maxFret - b.maxFret;
  });

  return results;
}

// ============================================
// GENERADOR EXPLORADOR (ALGORÍTMICO)
// ============================================

function getIntervalSemitones(quality: ExtendedChordQuality): number[] {
  const def = getChordDef(quality);
  return def?.intervals || [0, 4, 7];
}

function getIntervalDegrees(quality: ExtendedChordQuality): string[] {
  const def = getChordDef(quality);
  return def?.degreeNames || ["R", "3", "5"];
}

function getInversionOrder(inv: InversionType, intervals: number[], degrees: string[]) {
  switch (inv) {
    case "fundamental":
      return { intervals, degrees };
    case "first":
      return {
        intervals: [...intervals.slice(1), intervals[0] + 12],
        degrees: [...degrees.slice(1), degrees[0]],
      };
    case "second":
      return {
        intervals: [...intervals.slice(2), intervals[0] + 12, intervals[1] + 12],
        degrees: [...degrees.slice(2), degrees[0], degrees[1]],
      };
    case "third":
      return {
        intervals: [...intervals.slice(3), intervals[0] + 12, intervals[1] + 12, intervals[2] + 12],
        degrees: [...degrees.slice(3), degrees[0], degrees[1], degrees[2]],
      };
  }
}

function applyVoicingType(
  notes: { string: number; fret: number; note: NoteName; degree: string }[],
  type: ExplorerFilter["voicingType"]
): typeof notes {
  if (type === "all" || notes.length <= 2) return notes;

  // Close: notas en cuerdas consecutivas
  if (type === "close") {
    return notes.filter((_, i) => i < 4); // máx 4 notas
  }

  // Spread: saltar cuerdas
  if (type === "spread") {
    const spread: typeof notes = [];
    for (let i = 0; i < notes.length; i += 2) {
      spread.push(notes[i]);
    }
    return spread.length >= 3 ? spread : notes;
  }

  // Drop2: bajar la 2da nota más aguda una octava (omitida en guitarra = quitar nota aguda)
  if (type === "drop2" && notes.length >= 4) {
    const reordered = [...notes];
    // En guitarra: drop2 = quitar la segunda cuerda más aguda y mantener grave
    reordered.splice(reordered.length - 2, 1);
    return reordered;
  }

  // Drop3: similar, quitar tercera nota aguda
  if (type === "drop3" && notes.length >= 4) {
    const reordered = [...notes];
    reordered.splice(reordered.length - 3, 1);
    return reordered;
  }

  return notes;
}

// ============================================
// SCORING DE CERCANÍA VERTICAL
// ============================================

interface ScoredVoicing {
  result: ChordVoicingResult;
  score: number;
}

/**
 * Calcula qué tan "compacto verticalmente" es un acorde.
 * Premia:
 *   - Span pequeño (trastes cercanos)
 *   - Cuerdas consecutivas (sin saltos grandes)
 *   - Notas agrupadas en clusters de trastes
 *   - Posiciones bajas en el mástil (más fáciles)
 *   - Raíz en la cuerda más grave (para fundamental)
 *   - Cuerdas al aire (bonus moderado)
 * Penaliza:
 *   - Span > 3 trastes
 *   - Saltos de cuerda > 2
 *   - Trastes muy altos (>9)
 *   - Dedos muy separados (stretch imposible)
 */
function scoreVerticalProximity(
  notes: { string: number; fret: number; degree: string }[],
  filter: ExplorerFilter
): number {
  let score = 100;

  const pressed = notes.filter((n) => n.fret > 0);
  const frets = pressed.map((n) => n.fret);
  const strings = notes.map((n) => n.string).sort((a, b) => b - a);
  const hasOpen = notes.some((n) => n.fret === 0);

  // 1. SPAN: la regla más importante
  if (frets.length > 1) {
    const span = Math.max(...frets) - Math.min(...frets);
    if (span <= 2) score += 40;
    else if (span <= 3) score += 20;
    else if (span <= 4) score += 0;
    else score -= (span - 4) * 25;
  }

  // 2. CONTINUIDAD DE CUERDAS: penalizar saltos
  for (let i = 1; i < strings.length; i++) {
    const gap = strings[i - 1] - strings[i];
    if (gap === 1) score += 8;
    else if (gap === 2) score += 2;
    else if (gap >= 3) score -= (gap - 2) * 15;
  }

  // 3. CLUSTER DE TRASTES: notas en el mismo traste o adyacentes
  const fretSet = new Set(frets);
  if (fretSet.size === 1 && frets.length >= 3) score += 30; // cejilla perfecta
  else if (fretSet.size <= 2) score += 15;
  else if (fretSet.size <= 3) score += 5;

  // 4. POSICIÓN EN EL MÁSTIL: más bajo = mejor
  const avgFret = frets.length > 0 ? frets.reduce((a, b) => a + b, 0) / frets.length : 0;
  if (avgFret <= 3) score += 20;
  else if (avgFret <= 5) score += 10;
  else if (avgFret <= 7) score += 0;
  else if (avgFret <= 9) score -= 10;
  else score -= 20;

  // 5. STRETCH DE DEDOS: penalizar si hay muchos trastes de diferencia entre cuerdas consecutivas
  for (let i = 1; i < notes.length; i++) {
    const prev = notes[i - 1];
    const curr = notes[i];
    const fretDiff = Math.abs(curr.fret - prev.fret);
    const stringDiff = Math.abs(curr.string - prev.string);
    if (stringDiff === 1 && fretDiff > 3) score -= (fretDiff - 3) * 10;
  }

  // 6. BONUS CUERDAS AL AIRE (moderado, no queremos que domine)
  if (hasOpen) score += 5;

  // 7. RAÍZ EN CUERDA GRAVE (para fundamental)
  if (filter.inversion === "fundamental") {
    const rootNote = notes.find((n) => n.degree === "R");
    if (rootNote && rootNote.string >= 4) score += 10;
  }

  // 8. Penalizar trastes presionados muy dispersos verticalmente
  // (ej: traste 1 y traste 12 en el mismo acorde)
  if (frets.length > 1) {
    const minF = Math.min(...frets);
    const maxF = Math.max(...frets);
    if (maxF - minF > 5) score -= 50;
    if (maxF - minF > 7) score -= 100;
  }

  // 9. Densidad: más notas en menos cuerdas = más denso = mejor para guitarra
  const stringSpan = Math.max(...strings) - Math.min(...strings);
  if (stringSpan <= 2) score += 10;
  else if (stringSpan <= 3) score += 5;

  return score;
}

/**
 * Para evitar explosión combinatorial, filtramos las opciones por voz
 * para quedarnos solo con las posiciones más prometedoras.
 */
function pruneVoiceOptions(
  options: { string: number; fret: number; note: NoteName; degree: string }[],
  maxPerVoice: number = 6
): typeof options {
  // Ordenar por: traste bajo primero, luego cuerda grave primero
  const sorted = [...options].sort((a, b) => {
    if (a.fret !== b.fret) return a.fret - b.fret;
    return b.string - a.string; // cuerda grave primero
  });
  return sorted.slice(0, maxPerVoice);
}

/**
 * Nuevo generador explorador basado en cuerdas seleccionadas por el usuario.
 *
 * Lógica:
 * 1. El usuario selecciona qué cuerdas quiere usar (ej: [3,2,1])
 * 2. Ordenamos de grave a aguda: [3,2,1]
 * 3. Según la inversión, la NOTA BAJA va en la cuerda más grave seleccionada:
 *    - Fundamental: Raíz
 *    - 1ª Inv: 3ra (intervalo índice 1)
 *    - 2ª Inv: 5ta (intervalo índice 2)
 *    - 3ª Inv: 7ma/extensión (intervalo índice 3)
 * 4. Buscamos todas las posiciones de la nota baja en esa cuerda
 * 5. Para cada posición, distribuimos las demás notas en las cuerdas restantes
 *    hacia arriba, buscando la posición más cercana posible.
 */
export function generateExplorerVoicings(
  root: NoteName,
  quality: ExtendedChordQuality,
  filter: ExplorerFilter
): ChordVoicingResult[] {
  const rawIntervals = getIntervalSemitones(quality);
  const rawDegrees = getIntervalDegrees(quality);
  const inv = getInversionOrder(filter.inversion, rawIntervals, rawDegrees);

  const rootSemi = noteToSemitones(root);
  const useFlats = shouldUseFlats(root, quality.includes("m") && !quality.includes("maj") ? "minor" : "major");

  // Cuerdas seleccionadas, ordenadas de grave a aguda
  const selectedStrings = [...filter.selectedStrings].sort((a, b) => b - a);
  if (selectedStrings.length === 0) return [];

  const bassString = selectedStrings[0]; // cuerda más grave
  const trebleStrings = selectedStrings.slice(1); // cuerdas restantes

  // Determinar qué nota (índice del intervalo) va en la cuerda grave según inversión
  let bassVoiceIdx = 0;
  switch (filter.inversion) {
    case "fundamental": bassVoiceIdx = 0; break;
    case "first": bassVoiceIdx = 1; break;
    case "second": bassVoiceIdx = 2; break;
    case "third": bassVoiceIdx = 3; break;
  }
  if (bassVoiceIdx >= inv.intervals.length) bassVoiceIdx = inv.intervals.length - 1;

  // Buscar todas las posiciones de la nota baja en la cuerda grave
  const bassNoteSemi = (rootSemi + inv.intervals[bassVoiceIdx]) % 12;
  const bassDegree = inv.degrees[bassVoiceIdx];

  const bassPositions: { fret: number; note: NoteName }[] = [];
  for (let f = 0; f <= filter.maxFret; f++) {
    const pos = getNoteAtPosition(bassString, f);
    if (noteToSemitones(pos.note) === bassNoteSemi) {
      bassPositions.push({
        fret: f,
        note: semitonesToNote(noteToSemitones(pos.note), useFlats),
      });
    }
  }

  if (bassPositions.length === 0) return [];

  // Para las voces restantes, buscamos notas en las cuerdas agudas
  const remainingVoices = inv.intervals
    .map((interval, idx) => ({ interval, degree: inv.degrees[idx], idx }))
    .filter((v) => v.idx !== bassVoiceIdx);

  const scoredResults: ScoredVoicing[] = [];

  for (const bassPos of bassPositions) {
    // Para cada posición de la nota baja, intentar colocar las demás notas
    // en las cuerdas restantes de forma cercana

    const currentNotes: { string: number; fret: number; note: NoteName; degree: string }[] = [
      { string: bassString, fret: bassPos.fret, note: bassPos.note, degree: bassDegree },
    ];

    let failed = false;

    for (const voice of remainingVoices) {
      const targetSemi = (rootSemi + voice.interval) % 12;
      let bestCandidate: { string: number; fret: number; note: NoteName } | null = null;
      let bestScore = Infinity;

      for (const s of trebleStrings) {
        for (let f = 0; f <= filter.maxFret; f++) {
          const pos = getNoteAtPosition(s, f);
          if (noteToSemitones(pos.note) !== targetSemi) continue;

          // Calcular distancia al bajo (horizontal + vertical)
          const fretDist = Math.abs(f - bassPos.fret);
          const stringDist = Math.abs(s - bassString);

          // Score: preferir misma cuerda (imposible), luego cuerda adyacente + traste cercano
          let score = fretDist * 2 + stringDist * 3;

          // Penalizar fuertemente si ya usamos esa cuerda
          if (currentNotes.some((n) => n.string === s)) {
            score += 1000;
            continue;
          }

          // Penalizar si excede el span permitido
          const allFrets = [...currentNotes.map((n) => n.fret), f].filter((fr) => fr > 0);
          if (allFrets.length > 1) {
            const span = Math.max(...allFrets) - Math.min(...allFrets);
            if (span > filter.maxSpan) {
              score += 500;
            }
          }

          // Penalizar cuerdas al aire si no están permitidas
          if (f === 0 && !filter.allowOpenStrings) {
            score += 200;
          }

          if (score < bestScore) {
            bestScore = score;
            bestCandidate = { string: s, fret: f, note: semitonesToNote(noteToSemitones(pos.note), useFlats) };
          }
        }
      }

      if (!bestCandidate || bestScore > 800) {
        failed = true;
        break;
      }

      currentNotes.push({
        string: bestCandidate.string,
        fret: bestCandidate.fret,
        note: bestCandidate.note,
        degree: voice.degree,
      });
    }

    if (failed) continue;

    // Verificar span final
    const pressed = currentNotes.filter((n) => n.fret > 0);
    const frets = pressed.map((n) => n.fret);
    const span = frets.length > 1 ? Math.max(...frets) - Math.min(...frets) : 0;
    if (span > filter.maxSpan) continue;

    const hasOpen = currentNotes.some((n) => n.fret === 0);
    if (!filter.allowOpenStrings && hasOpen) continue;

    const hasBarre = pressed.length >= 2 && new Set(pressed.map((n) => n.fret)).size < pressed.length;
    if (!filter.allowBarre && hasBarre) continue;

    // Ordenar de grave a aguda para presentación
    currentNotes.sort((a, b) => b.string - a.string);

    const neckPositions: NeckPosition[] = currentNotes.map((c, i) => ({
      string: c.string,
      fret: c.fret,
      note: c.note,
      degree: c.degree as any,
      highlight: c.degree === "R" ? "root" : c.degree === "5" || c.degree === "b5" || c.degree === "#5" ? "fifth" : "third",
      finger: c.fret === 0 ? 0 : Math.min(i + 1, 4),
    }));

    // Cuerdas no seleccionadas = mutadas
    const muted: number[] = [];
    for (let s = 6; s >= 1; s--) {
      if (!selectedStrings.includes(s)) muted.push(s);
    }

    // Detectar cejilla
    const fretCounts = new Map<number, number>();
    for (const p of pressed) {
      fretCounts.set(p.fret, (fretCounts.get(p.fret) || 0) + 1);
    }
    let barre: { fromString: number; toString: number; fret: number } | undefined;
    for (const [fret, count] of fretCounts) {
      if (count >= 2) {
        const atFret = currentNotes.filter((c) => c.fret === fret).sort((a, b) => b.string - a.string);
        if (atFret.length >= 2) {
          barre = { fromString: atFret[0].string, toString: atFret[atFret.length - 1].string, fret };
        }
      }
    }

    const result: ChordVoicingResult = {
      positions: neckPositions,
      mutedStrings: muted,
      barre,
      stringCount: currentNotes.length,
      hasOpenStrings: hasOpen,
      maxFret: Math.max(...currentNotes.map((c) => c.fret)),
      span,
    };

    const proximityScore = scoreVerticalProximity(currentNotes, filter);
    scoredResults.push({ result, score: proximityScore });
  }

  // Ordenar por score de cercanía vertical (mayor = mejor)
  scoredResults.sort((a, b) => b.score - a.score);

  return scoredResults.slice(0, 25).map((s) => s.result);
}

// ============================================
// FUNCIONES DE AYUDA PARA LA UI
// ============================================

export function getQualityCategories() {
  return [
    { id: "triada", label: "Tríadas" },
    { id: "tetrada", label: "Tétradas" },
    { id: "tension", label: "Tensiones (9, 11, 13)" },
    { id: "alterada", label: "Alteradas" },
  ];
}

export function getQualitiesByCategory(category: string): ExtendedChordQuality[] {
  return CHORD_CATALOG.filter((c) => c.category === category).map((c) => c.quality);
}
